import React, { useState, useCallback, useRef, useEffect } from 'react';
import { message, Modal, Input, Button, Space, Typography, Tag, Select, Form, Row, Col, Upload, Tabs, List, Avatar, Tooltip, Dropdown } from 'antd';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { 
  EditOutlined, 
  SaveOutlined, 
  CloseOutlined, 
  DeleteOutlined, 
  StarOutlined, 
  PushpinOutlined, 
  UserOutlined, 
  ClockCircleOutlined, 
  TagOutlined, 
  PlusOutlined, 
  MinusOutlined,
  LinkOutlined,
  FileTextOutlined,
  BranchesOutlined,
  RobotOutlined,
  UploadOutlined,
  GlobalOutlined,
  CopyOutlined,
  ShareAltOutlined,
  TeamOutlined,
  FolderOutlined,
  FolderOpenOutlined
} from '@ant-design/icons';
import MDEditor from '@uiw/react-md-editor';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useNoteStore } from '../stores/noteStore';
import { useSearchStore } from '../stores/searchStore';
import { usePrivateKnowledgeBase } from '../contexts/PrivateKnowledgeBaseContext';
import { useCollaborativeKnowledgeBase } from '../contexts/CollaborativeKnowledgeBaseContext';
import { useNoteList } from '../contexts/NoteListContext';
import {
  getSelectedNote,
  getSelectedFolder,
  getSelectedTopic,
  getSelectedKB,
  getSearchKeyword,
  getPagination,
  getSelectedTag,
  setSelectedNote,
  setSelectedTag,
  setNoteStateChange
} from '../hooks/useDashboardGlobalVar';
import apiClient from '../services/api';
import i18n from '../i18n';
// CSS import removed - this file doesn't need NoteDetail.css

const { TextArea } = Input;
const { Text, Title, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
// Layout components removed - not used in this utility file
const { confirm } = Modal;

// Format time display
export const formatTime = (timeString) => {
  if (!timeString) return 'Unknown time';
  const date = new Date(timeString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minutes ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// 获取字符串的显示宽度（用于截断）
export const getDisplayWidth = (str) => {
  if (!str) return 0;
  let width = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    if (char > 255) {
      width += 2; // 中文字符
    } else {
      width += 1; // 英文字符
    }
  }
  return width;
};

// 根据显示宽度截断字符串
export const truncateByDisplayWidth = (str, maxWidth) => {
  if (!str) return '';
  if (getDisplayWidth(str) <= maxWidth) return str;
  
  let result = '';
  let currentWidth = 0;
  
  for (let i = 0; i < str.length; i++) {
    const char = str[i];
    const charWidth = str.charCodeAt(i) > 255 ? 2 : 1;
    
    if (currentWidth + charWidth > maxWidth) {
      break;
    }
    
    result += char;
    currentWidth += charWidth;
  }
  
  return result + '...';
};

// 截断标题
export const truncateTitle = (title, maxDisplayWidth = 40) => {
  return truncateByDisplayWidth(title, maxDisplayWidth);
};

// 截断内容
export const truncateContent = (content, maxDisplayWidth = 150) => {
  return truncateByDisplayWidth(content, maxDisplayWidth);
};

// 检查是否为笔记所有者
export const isNoteOwner = (note, user) => {
  if (!note || !user) {
    console.log('[isNoteOwner] 参数检查失败:', { hasNote: !!note, hasUser: !!user });
    return false;
  }
  
  // 检查 user 是否有 id 字段
  if (!user.id) {
    console.warn('[isNoteOwner] user 对象缺少 id 字段:', { user, noteId: note.id });
    return false;
  }
  
  const isOwner = note.owner_id === user.id || note.user_id === user.id;
  console.log('[isNoteOwner] 权限判断:', {
    noteId: note.id,
    noteOwnerId: note.owner_id,
    noteUserId: note.user_id,
    userId: user.id,
    isOwner
  });
  
  return isOwner;
};

/**
 * 安全地渲染 Markdown 内容为 HTML
 * @param {string} markdownContent - Markdown 格式的内容
 * @returns {string} - 清理后的安全 HTML 字符串
 */
export const renderMarkdownSafely = (markdownContent) => {
  if (!markdownContent || typeof markdownContent !== 'string') {
    return '';
  }

  try {

    const escapedContent = markdownContent.replace(/\*\*\*/g, '\\*\\*\\*');

    // 配置 marked
    marked.setOptions({
      gfm: true,
      breaks: true
    });

    // 1. 使用 Marked.js 将处理过的 Markdown 转换为 HTML
    const dirtyHtml = marked.parse(escapedContent);

    // 2. 手动处理 markdown 格式
    let processedHtml = dirtyHtml;
    // 处理 ***text***（粗斜体）
    processedHtml = processedHtml.replace(/\*\*\*([^*\n]+?)\*\*\*/g, '<strong><em>$1</em></strong>');
    // 处理 **text**（粗体）
    processedHtml = processedHtml.replace(/\*\*([^*\n]+?)\*\*/g, '<strong>$1</strong>');

    // 3. 使用 DOMPurify 清理 HTML
    const cleanHtml = DOMPurify.sanitize(processedHtml, {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'u', 's', 'del', 'ins',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
        'a', 'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'hr', 'div', 'span'
      ],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'id', 'target', 'rel'],
      ALLOW_DATA_ATTR: true
    });

    return cleanHtml;
  } catch (error) {
    console.error('[DashboardUtils] Markdown 渲染失败:', error);
    // 如果渲染失败，返回原始内容（已转义）
    return DOMPurify.sanitize(markdownContent);
  }
};

/**
 * 判断内容是否为文件路径
 * @param {string} content - 内容字符串
 * @returns {boolean} - 是否为文件路径
 */
export const isContentFilePath = (content) => {
  if (!content) return false;
  return (
    content.includes('uploads/') || 
    content.includes('cloud/') ||
    content.endsWith('.md') ||
    content.endsWith('.txt')
  );
};

/**
 * 处理 note.content，如果是文件路径则读取，否则直接渲染 Markdown
 * @param {string} noteContent - note.content 的值
 * @param {object} apiClient - API 客户端实例
 * @returns {Promise<{content: string, needsLoading: boolean}>} - 返回处理后的内容和加载状态
 */
const processNoteContent = async (noteContent, apiClient) => {
  if (!noteContent) {
    return { content: '', needsLoading: false };
  }

  if (isContentFilePath(noteContent)) {
    // note.content 是文件路径，需要从服务器读取
    try {
      const contentRes = await apiClient.post('/notes/content', { contentPath: noteContent, mdOnly: false });
      const htmlContent = contentRes.data.content || '';
      // 接口已返回 HTML 格式，直接使用
      return { content: htmlContent, needsLoading: false };
    } catch (error) {
      console.error('[DashboardUtils] 读取 note.content 失败:', error);
      return { content: '', needsLoading: false };
    }
  } else {
    // note.content 不是文件路径，直接渲染 Markdown
    const htmlContent = renderMarkdownSafely(noteContent);
    return { content: htmlContent, needsLoading: false };
  }
};

/**
 * 加载并处理笔记内容
 * 优先使用 content_html，如果不存在或读取失败，则使用 note.content
 * @param {object} note - 笔记对象
 * @param {object} apiClient - API 客户端实例
 * @param {function} t - 翻译函数
 * @param {function} isFilePath - 判断是否为文件路径的函数
 * @returns {Promise<{content: string, needsLoading: boolean}>} - 返回处理后的内容和加载状态
 */
export const loadNoteContent = async (note, apiClient, t, isFilePath) => {
  if (!note) {
    return { content: '', needsLoading: false };
  }

  // 优先使用 content_html（HTML版本）
  const contentHtml = note.content_html;
  
  if (contentHtml) {
    if (isFilePath(contentHtml)) {
      // content_html 是文件路径，读取 HTML 文件内容
      try {
        const contentResponse = await apiClient.post('/notes/content', { contentPath: contentHtml, mdOnly: false });
        const rawContent = contentResponse.data.content || '';
        
        // 如果读取的内容为空或长度小于10，尝试使用 note.content
        if (!rawContent || rawContent.length < 10) {
          if (note.content) {
            const result = await processNoteContent(note.content, apiClient);
            return { 
              content: result.content || (t ? t('dashboard:no_content', { defaultValue: 'No content' }) : 'No content'), 
              needsLoading: false 
            };
          } else {
            return { 
              content: t ? t('dashboard:no_content', { defaultValue: 'No content' }) : 'No content', 
              needsLoading: false 
            };
          }
        } else {
          // content_html 读取成功，直接使用
          return { content: rawContent, needsLoading: false };
        }
      } catch (error) {
        console.error('[DashboardUtils] 读取 content_html 失败:', error);
        // 读取失败，尝试使用 note.content
        if (note.content) {
          const result = await processNoteContent(note.content, apiClient);
          return { 
            content: result.content || (t ? t('dashboard:no_content', { defaultValue: 'No content' }) : 'No content'), 
            needsLoading: false 
          };
        } else {
          return { 
            content: t ? t('dashboard:no_content', { defaultValue: 'No content' }) : 'No content', 
            needsLoading: false 
          };
        }
      }
    } else {
      // content_html 已经是 HTML 字符串，直接使用
      return { content: contentHtml, needsLoading: false };
    }
  } else {
    // content_html 不存在，检查 note.content
    if (note.content) {
      const result = await processNoteContent(note.content, apiClient);
      return { 
        content: result.content || (t ? t('dashboard:no_content', { defaultValue: 'No content' }) : 'No content'), 
        needsLoading: false 
      };
    } else {
      // 都没有内容
      return { 
        content: t ? t('dashboard:no_content', { defaultValue: 'No content' }) : 'No content', 
        needsLoading: false 
      };
    }
  }
};

// Helper function to detect and handle S3 bucket URLs and uploaded files
export const handleSourceUrl = (sourceUrl) => {
  if (!sourceUrl) return null;
  
  // 处理 uploads/ 或 cloud/ 开头的文件路径，在新窗口中打开下载页面
  if (sourceUrl.startsWith('uploads/') || sourceUrl.startsWith('cloud/')) {
    return {
      isS3File: true,
      isPrivateBucket: true,
      url: sourceUrl,
      onClick: (e) => {
        e.preventDefault();
        // 在新窗口中打开下载页面
        const downloadUrl = `/source-download?path=${encodeURIComponent(sourceUrl)}`;
        window.open(downloadUrl, '_blank', 'width=600,height=400');
      }
    }
  }
  
  // For regular URLs, open in new tab
  return {
    isS3File: false,
    isPrivateBucket: false,
    url: sourceUrl,
    onClick: null
  };
};

// 创建Dashboard处理器的工厂函数
export const createDashboardHandlers = (deps) => {
  const {
    user,
    logout,
    navigate,
    setSelectedNote,
    editTitle,
    setEditTitle,
    editSummary,
    setEditSummary,
    editContent,
    setEditContent,
    isEditing,
    setIsEditing,
    isAddingTag,
    setIsAddingTag,
    newTagName,
    setNewTagName,
    userHistoryTags,
    setUserHistoryTags,
    isLoadingHistoryTags,
    setIsLoadingHistoryTags,
    refreshKnowledgeBasesAndTopics,
    fetchNotesStats,
    deleteNote,
    togglePinNote,
    currentFilterState,
    realContent
  } = deps;

  // 根据当前筛选状态刷新笔记列表的辅助函数
  const refreshNotesWithCurrentFilter = async () => {
    if (currentFilterState) {
      const { selectedFolder, selectedTag, selectedTopic } = currentFilterState;
      const currentTags = selectedTag ? [selectedTag.name] : [];
      const currentTopicId = selectedTopic ? selectedTopic.topic_id : null;
      const filterType = selectedFolder === 'private' ? 'private' : 
                        selectedFolder === 'collaborative' ? 'collaborative' :
                        selectedFolder === 'pinned' ? 'pinned' :
                        selectedFolder === 'recent' ? 'recent' : 'all';
      
      console.log('[前端] 使用当前筛选状态刷新笔记列表:', {
        selectedFolder,
        selectedTag: selectedTag?.name,
        selectedTopic: selectedTopic?.name,
        filterType,
        currentTags
      });
      
      // fetchNotes function call removed - this should be passed as a parameter
    } else {
      // 如果没有筛选状态信息，使用默认的全部笔记
      console.log('[前端] 使用默认筛选条件刷新笔记列表');
      // fetchNotes function call removed - this should be passed as a parameter
    }
  };

  const handleLogout = () => {
    console.log('用户点击退出登录');
    // logout function call removed - this should be passed as a parameter
      message.success(i18n.t('messages:logout_success', { defaultValue: 'Logged out successfully' }));
    // navigate function call removed - this should be passed as a parameter
  };

  const handleEdit = () => {
    const currentSelectedNote = getSelectedNote();
    if (!currentSelectedNote) {
      message.warning(i18n.t('messages:please_select_note'));
      return;
    }
    
    // 权限检查：只有笔记所有者才能编辑
    if (currentSelectedNote.owner_id !== user?.id) {
      message.error(i18n.t('messages:no_permission_edit'));
      return;
    }
    
    // 设置编辑状态和内容
    setEditTitle(currentSelectedNote.title || '');
    setEditSummary(currentSelectedNote.summary || '');
    // 修改点：优先使用realContent作为编辑内容
    const contentToEdit = realContent || currentSelectedNote.content || '';
    setEditContent(contentToEdit);
    setIsEditing(true);
    
    console.log('[DashboardUtils] 开始编辑笔记:', {
      noteId: currentSelectedNote.id,
      title: currentSelectedNote.title,
      realContent: realContent?.substring(0, 100),
      originalContent: currentSelectedNote.content?.substring(0, 100),
      contentToEdit: contentToEdit?.substring(0, 100)
    });
  };

  const handleSave = async (editTitle, editSummary, editContent) => {
    const currentSelectedNote = getSelectedNote();
    console.log('[前端] handleSave 开始执行，参数:', {
      editTitle,
      editSummary,
      editContent,
      selectedNoteId: currentSelectedNote?.id,
      selectedNoteTitle: currentSelectedNote?.title,
      selectedNoteContent: currentSelectedNote?.content
    });

    // 验证title和summary不能为空
    if (!editTitle || !editTitle.trim()) {
      message.error(i18n.t('messages:enter_title'));
      return;
    }
    if (!editSummary || !editSummary.trim()) {
      message.error(i18n.t('messages:enter_summary'));
      return;
    }

    const trimmedTitle = editTitle.trim();
    const trimmedSummary = editSummary.trim();
    const trimmedContent = editContent ? editContent.trim() : '';

    // 检查是否有修改
    const titleChanged = trimmedTitle !== currentSelectedNote.title;
    const summaryChanged = trimmedSummary !== currentSelectedNote.summary;
    const contentChanged = trimmedContent !== (currentSelectedNote.content || '');

    console.log('[前端] 修改检查结果:', {
      titleChanged,
      summaryChanged,
      contentChanged,
      originalContent: currentSelectedNote.content,
      newContent: trimmedContent
    });

    if (!titleChanged && !summaryChanged && !contentChanged) {
      message.info(i18n.t('messages:no_changes'));
      // setIsEditing function call removed - this should be passed as a parameter
      return;
    }

    try {
      // 显示加载提示
      const hide = message.loading(i18n.t('messages:saving'), 0);
      
      // 构建更新数据
      const updateData = {
        title: trimmedTitle,
        summary: trimmedSummary
      };

      console.log('[前端] 准备调用updateNote API，数据:', updateData);

      // 调用更新API
      const result = await apiClient.put(`/notes/${currentSelectedNote.id}`, updateData);
      
      console.log('[前端] updateNote API 返回结果:', result);
      
      // 隐藏加载提示
      hide();
      
      if (result.status === 200 && result.data) {
        console.log('[前端] 更新成功，返回的笔记数据:', result.data);
        message.success(i18n.t('messages:save_success'));
        setIsEditing(false);
        
        // 重要修复：重新从后台获取最新的笔记信息，确保详细页面显示最新数据
        try {
          console.log('[前端] 开始重新获取最新笔记信息...');
          const refreshResponse = await apiClient.get(`/notes/${currentSelectedNote.id}`);
          
          if (refreshResponse.data && refreshResponse.data.success && refreshResponse.data.data) {
            // 使用最新的笔记数据更新状态
            const latestNoteData = refreshResponse.data.data;
            console.log('[前端] 成功获取最新笔记信息:', {
              noteId: latestNoteData.id,
              title: latestNoteData.title,
              summary: latestNoteData.summary,
              summaryLength: latestNoteData.summary?.length || 0,
              contentLength: latestNoteData.content?.length || 0,
              oldSummary: currentSelectedNote.summary,
              oldSummaryLength: currentSelectedNote.summary?.length || 0
            });
            
            // 检查摘要是否真的更新了
            if (latestNoteData.summary !== currentSelectedNote.summary) {
              console.log('[前端] 摘要已更新:', {
                old: currentSelectedNote.summary?.substring(0, 100),
                new: latestNoteData.summary?.substring(0, 100)
              });
            } else {
              console.log('[前端 line323] 警告：摘要没有更新，可能是后端问题');
            }
            
            // 重要修复：直接调用全局的 setSelectedNote 函数，确保全局状态更新
            try {
              const { setSelectedNote: globalSetSelectedNote } = await import('../hooks/useDashboardGlobalVar');
              console.log('[前端] 成功导入全局 setSelectedNote 函数');
              
              // 调用全局的 setSelectedNote 更新全局状态
              globalSetSelectedNote(latestNoteData, 'DashboardUtils-handleSave-success');
              console.log('[前端 line333] 已调用全局 setSelectedNote，新的笔记数据:', {
                id: latestNoteData.id,
                title: latestNoteData.title,
                summary: latestNoteData.summary?.substring(0, 100)
              });
              
              // 同时调用传入的 setSelectedNote 更新本地状态（保持兼容性）
              if (setSelectedNote && typeof setSelectedNote === 'function') {
                setSelectedNote(latestNoteData);
                console.log('[前端 line343] 已调用本地 setSelectedNote 保持兼容性');
              }
            } catch (importError) {
              console.error('[前端] 导入全局 setSelectedNote 失败，使用传入的 setSelectedNote:', importError);
              // 如果导入失败，使用传入的 setSelectedNote
              if (setSelectedNote && typeof setSelectedNote === 'function') {
                setSelectedNote(latestNoteData);
              }
            }
            
            // 触发笔记状态变化，通知系统笔记已更新，激活混合搜索
            if (titleChanged || summaryChanged || contentChanged) {
              console.log('[前端 line353] 笔记内容已更新，触发状态变化通知');
              try {
                const { setNoteStateChange } = await import('../hooks/useDashboardGlobalVar');
                console.log('[前端 line357] 成功导入 setNoteStateChange 函数');
                setNoteStateChange(latestNoteData.id, 'update', 'DashboardUtils-handleSave-success');
                console.log('[前端 line359]************************ 已调用 setNoteStateChange，笔记ID:', latestNoteData.id);
                message.info(i18n.t('messages:note_updated'));
              } catch (error) {
                console.error('[前端 line362] 调用 setNoteStateChange 失败:', error);
              }
            }
          } else {
            console.warn('[前端 line366]************************ 重新获取笔记信息失败，使用API返回的数据:', refreshResponse.data);
            // 如果重新获取失败，使用API返回的数据
            try {
              const { setSelectedNote: globalSetSelectedNote } = await import('../hooks/useDashboardGlobalVar');
              globalSetSelectedNote(result.data, 'DashboardUtils-handleSave-fallback');
              
              if (setSelectedNote && typeof setSelectedNote === 'function') {
                setSelectedNote(result.data);
                console.log('[前端 line374]************************ 已调用 setSelectedNote，新的笔记数据:', {
                  id: result.data.id,
                  title: result.data.title,
                  summary: result.data.summary?.substring(0, 100)
                });
              }
            } catch (importError) {
              console.error('[前端 line381] 导入全局 setSelectedNote 失败，使用传入的 setSelectedNote:', importError);
              if (setSelectedNote && typeof setSelectedNote === 'function') {
                setSelectedNote(result.data);
              }
            }
            
            // 仍然触发状态变化通知
            if (titleChanged || summaryChanged || contentChanged) {
              try {
                const { setNoteStateChange } = await import('../hooks/useDashboardGlobalVar');
                setNoteStateChange(result.data.id, 'update', 'DashboardUtils-handleSave-fallback');
                console.log('[前端 line392]************************ 已调用 setNoteStateChange，笔记ID:', result.data.id);
              } catch (error) {
                console.error('[前端] 调用 setNoteStateChange 失败:', error);
              }
            }
          }
        } catch (refreshError) {
          console.error('[前端] 重新获取笔记信息时出错:', refreshError);
          // 如果重新获取失败，使用API返回的数据
          try {
            const { setSelectedNote: globalSetSelectedNote } = await import('../hooks/useDashboardGlobalVar');
            globalSetSelectedNote(result.data, 'DashboardUtils-handleSave-error');
            
            if (setSelectedNote && typeof setSelectedNote === 'function') {
              setSelectedNote(result.data);
            }
          } catch (importError) {
            console.error('[前端] 导入全局 setSelectedNote 失败，使用传入的 setSelectedNote:', importError);
            if (setSelectedNote && typeof setSelectedNote === 'function') {
              setSelectedNote(result.data);
            }
          }
          
          // 仍然触发状态变化通知
          if (titleChanged || summaryChanged || contentChanged) {
            try {
              const { setNoteStateChange } = await import('../hooks/useDashboardGlobalVar');
              setNoteStateChange(result.data.id, 'update', 'DashboardUtils-handleSave-error');
            } catch (error) {
              console.error('[前端] 调用 setNoteStateChange 失败:', error);
            }
          }
        }
      } else {
        console.error('[前端] 更新失败:', result.error || result.message);
        message.error(i18n.t('messages:save_failed') + ': ' + (result.error || result.message || i18n.t('messages:unknown_error')));
      }
    } catch (error) {
      message.error(i18n.t('messages:save_failed') + ': ' + (error.response?.data?.message || error.message || i18n.t('messages:please_retry')));
    }
  };

  const handlePin = async () => {
    const currentSelectedNote = getSelectedNote();
    if (!currentSelectedNote) return;
    
    // 权限检查：只有笔记所有者才能置顶
    if (!isNoteOwner(currentSelectedNote, user)) {
      message.error(i18n.t('messages:no_permission_pin'));
      return;
    }
    
    try {
      console.log('开始置顶操作, 当前笔记:', currentSelectedNote);
      console.log('[DashboardUtils] togglePinNote 函数:', typeof togglePinNote, togglePinNote);
      
      if (!togglePinNote || typeof togglePinNote !== 'function') {
        throw new Error('togglePinNote function was not properly passed or is not a function');
      }
      
      // 显示加载提示
      const hide = message.loading(currentSelectedNote.is_pinned ? i18n.t('messages:loading') : i18n.t('messages:loading'), 0);
      
      try {
        // 调用 Store 的 togglePinNote 方法
        console.log('[DashboardUtils] 调用 togglePinNote:', currentSelectedNote.id, currentSelectedNote.is_pinned);
        const result = await togglePinNote(currentSelectedNote.id, currentSelectedNote.is_pinned);
        
        console.log('[DashboardUtils] togglePinNote 返回结果:', result);
        
        // 隐藏加载提示
        hide();
        
        if (result.success) {
          message.success(currentSelectedNote.is_pinned ? i18n.t('notes:unpin_success', { defaultValue: 'Unpinned successfully' }) : i18n.t('notes:pin_success', { defaultValue: 'Pinned successfully' }));
          
          console.log('[前端] 置顶操作成功，笔记状态已更新，UI将自动刷新');
          
          // 更新 selectedNote 以反映最新的置顶状态，但不重复触发 noteStateChange
          if (result.data) {
            console.log('[DashboardUtils] 收到更新后的笔记数据，更新 selectedNote 状态');
            // 只更新 selectedNote，不更新 noteStateChange（避免重复触发）
            // noteStore.togglePinNote 已经通过 setNoteStateChange 触发了状态变化
            const { setSelectedNote } = await import('../hooks/useDashboardGlobalVar');
            setSelectedNote(result.data, 'DashboardUtils-handlePin-success');
          }
          
          // 刷新统计数据
          if (fetchNotesStats) {
            await fetchNotesStats();
          }
          
          // 注意：不再调用 refreshKnowledgeBasesAndTopics
          // 全局状态更新已经足够，监听器会自动处理相关更新
          // 避免重复操作和多次状态更新
        } else {
          message.error(result.message || i18n.t('messages:operation_failed'));
        }
        
      } catch (error) {
        // 隐藏加载提示
        hide();
        console.error('[DashboardUtils] togglePinNote 调用失败:', error);
        throw error;
      }
      
    } catch (error) {
      console.error('置顶操作错误:', error);
      
      let errorMessage = currentSelectedNote?.is_pinned ? i18n.t('messages:save_failed') + '：' : i18n.t('messages:save_failed') + '：';
      if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else if (error.response?.status) {
        errorMessage += `HTTP ${error.response.status} ${error.response.statusText}`;
      } else {
        errorMessage += error.message || i18n.t('messages:please_retry');
      }
      
      message.error(errorMessage);
    }
  };

  // 注意：is_public字段已不再使用，此功能已移除
  const handleTogglePublic = async () => {
    message.info(i18n.t('messages:operation_failed'));
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditTitle('');
    setEditSummary('');
    setEditContent('');
  };

  const handleDeleteNote = async () => {
    const currentSelectedNote = getSelectedNote();
    if (!currentSelectedNote) return;
    
    // 权限检查：只有笔记所有者才能删除
    if (currentSelectedNote.owner_id !== user?.id) {
      message.error(i18n.t('messages:permission_denied'));
      return;
    }
    
    try {
      console.log('开始删除操作, 当前笔记:', currentSelectedNote);
      
      // 显示确认对话框
      const confirmed = window.confirm(i18n.t('messages:confirm_delete'));
      if (!confirmed) return;
      
      // 显示加载提示
      const hide = message.loading(i18n.t('messages:deleting_note'), 0);
      
      try {
        // 调用noteStore的deleteNote方法，这会同时更新本地状态和调用API
        if (!deleteNote) {
          throw new Error('deleteNote function is not available');
        }
        
        const deleteResult = await deleteNote(currentSelectedNote.id);
        
        if (!deleteResult.success) {
          throw new Error(deleteResult.message || 'Delete failed');
        }
        
        console.log('删除API调用成功');
        
        // 隐藏加载提示
        hide();
        
        message.success(i18n.t('messages:delete_note_success'));
        
        // 等待一小段时间确保noteStore状态更新完成，然后清空选中笔记
        setTimeout(() => {
          console.log('[Dashboard] 删除后清空选中笔记，触发重新选择');
          setSelectedNote(null);
        }, 200);
        
        // 刷新整个 sidebar（包括统计数据、主题笔记数量、协作库笔记数量等）
        if (refreshKnowledgeBasesAndTopics) {
          console.log('[Dashboard] 删除笔记成功，开始刷新整个 sidebar...');
          await refreshKnowledgeBasesAndTopics();
          console.log('[Dashboard] sidebar 刷新完成');
        } else {
          // 回退方案：只刷新统计数据
          if (fetchNotesStats) {
            await fetchNotesStats();
          }
        }
        
      } catch (error) {
        // 隐藏加载提示
        hide();
        console.error('删除API调用详细错误:', error);
        console.error('删除错误响应:', error.response);
        throw error;
      }
      
    } catch (error) {
      console.error('删除笔记错误:', error);
      
      let errorMessage = i18n.t('messages:delete_note_failed', { error: '' });
      if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else if (error.response?.status) {
        errorMessage += `HTTP ${error.response.status} ${error.response.statusText}`;
      } else {
        errorMessage = i18n.t('messages:delete_note_failed', { error: error.message || i18n.t('messages:please_retry') });
      }
      
      message.error(errorMessage);
    }
  };

  const startCreateTimer = () => {
    // setIsCreatingNote function call removed - this should be passed as a parameter
    // setCreateTimer function call removed - this should be passed as a parameter
    const interval = setInterval(() => {
      // setCreateTimer function call removed - this should be passed as a parameter
    }, 1000);
    // setTimerInterval function call removed - this should be passed as a parameter
  };

  const handleCreateFromLink = async (values) => {
    try {
      console.log('***********handleCreateFromLink 从上传文件创建笔记:', values);
      console.log('***********handleCreateFromLink 接收到的values详细信息：', JSON.stringify(values, null, 2));
      console.log('***********handleCreateFromLink source_url字段：', values.source_url);
      console.log('***********handleCreateFromLink source_url类型：', typeof values.source_url);
      
      // 启动计时器
      startCreateTimer();
      // 保证 url 字段为字符串
      const url = values.url || '';

      try {
        // 使用配置好的apiClient进行链接提取
        //const extractResponse = await apiClient.post('/notes/extract-from-url', { 
        //  url: values.url 
        //});        
        //const extractedData = extractResponse.data;

        /*
                  // 使用配置好的apiClient而不是直接fetch
          const response = await apiClient.post('/notes', {
            title: values.title,
            content: values.content,
            source_url: values.source_url,
            source_type: 'text',
            kb_id: values.kb_id, // 传递知识库ID，让后端处理主题关联
          });
        */
        
        // 创建笔记，传递知识库ID让后端处理主题关联
        const dataToSend = {
          title: values.title || 'untitled',
          content: values.content || '',
          source_url: values.source_url || '',
          source_type: values.source_type || 'other',
          kb_id: values.kb_id
        };
        
        console.log('***********handleCreateFromLink 即将发送到后端的数据:', dataToSend);
        console.log('***********handleCreateFromLink 发送数据的source_url：', dataToSend.source_url);
        console.log('***********handleCreateFromLink 发送数据的source_url类型：', typeof dataToSend.source_url);
        console.log('***********handleCreateFromLink 发送数据的source_url长度：', dataToSend.source_url ? dataToSend.source_url.length : 0);
        
        // 验证URL格式
        if (dataToSend.source_url) {
          try {
            const urlObj = new URL(dataToSend.source_url);
            console.log('***********handleCreateFromLink URL验证成功：', urlObj.href);
          } catch (e) {
            console.error('***********handleCreateFromLink URL验证失败：', e.message);
          }
        }
        
        const createResponse = await apiClient.post('/notes', dataToSend);
        
        const newNote = createResponse.data;
        
        // 停止计时器
        // stopCreateTimer function call removed - this should be passed as a parameter
        
        message.success(i18n.t('messages:note_create_from_link_success', { defaultValue: 'Note created from link successfully!' }));
        // setIsCreateModalVisible function call removed - this should be passed as a parameter
        // linkForm.resetFields function call removed - this should be passed as a parameter
        /***/
        // 刷新笔记列表并选中新笔记 - 使用当前筛选条件
        await refreshNotesWithCurrentFilter();
        setSelectedNote(newNote);
        
        // 刷新左侧知识库和主题数据
        if (refreshKnowledgeBasesAndTopics) {
          await refreshKnowledgeBasesAndTopics();
        }
        
        // 刷新统计数据
        if (fetchNotesStats) {
          await fetchNotesStats();
        }
        /***/
      } catch (error) {
        // 停止计时器
        // stopCreateTimer function call removed - this should be passed as a parameter
        throw error;
      }
      
    } catch (error) {
      console.error('创建笔记错误:', error);
      // 确保计时器已停止
      // stopCreateTimer function call removed - this should be passed as a parameter
      message.error(i18n.t('messages:note_create_from_link_failed', {
        defaultValue: 'Creation failed: {{error}}',
        error: error.response?.data?.message || error.message || i18n.t('messages:check_link_valid', { defaultValue: 'Please check if the link is valid' })
      }));
    }
  };

  const handleCreateModalCancel = () => {
            // setIsCreateModalVisible function call removed - this should be passed as a parameter
        // createForm.resetFields function call removed - this should be passed as a parameter
        // linkForm.resetFields function call removed - this should be passed as a parameter
        // setCreateMode function call removed - this should be passed as a parameter
        // 停止计时器（如果正在运行）
        // stopCreateTimer function call removed - this should be passed as a parameter
  };

  const handleFileUpload = async (file) => {
    try {
      // TODO: 实现文件上传逻辑
      console.log('上传文件:', file);
      message.success(i18n.t('messages:file_upload_success', { defaultValue: 'File uploaded successfully, processing...' }));
      
      // 模拟文件处理
      const newNote = {
        id: Date.now().toString(),
        title: file.name,
        content: 'File content processing...',
        source_type: 'file',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      
      // setIsCreateModalVisible function call removed - this should be passed as a parameter
      // fetchNotes function call removed - this should be passed as a parameter
      setSelectedNote(newNote);
      
      return false; // 阻止默认上传行为
    } catch (error) {
      message.error(i18n.t('messages:file_upload_failed', {
        defaultValue: 'File upload failed: {{error}}',
        error: error.message
      }));
      return false;
    }
  };

  // 获取用户历史标签
  const handleFetchUserHistoryTags = async () => {
    if (!user?.id) return;
    
    try {
      setIsLoadingHistoryTags(true);
      const response = await apiClient.get('/notes/user/tags');
      console.log('[handleFetchUserHistoryTags] API响应:', response.data);
      
      if (response.data && Array.isArray(response.data)) {
        setUserHistoryTags(response.data);
        console.log('[handleFetchUserHistoryTags] 成功获取历史标签，数量:', response.data.length);
      } else {
        console.warn('[handleFetchUserHistoryTags] API返回数据格式异常:', response.data);
        setUserHistoryTags([]);
      }
    } catch (error) {
      console.error('获取历史标签失败:', error);
      setUserHistoryTags([]);
    } finally {
      setIsLoadingHistoryTags(false);
    }
  };

  // 点击历史标签快速添加
  const handleQuickAddTag = async (tagName) => {
    const currentSelectedNote = getSelectedNote();
    if (!currentSelectedNote || !tagName) return;
    
    // 验证标签长度，数据库字段限制为50个字符
    if (tagName.length > 50) {
      message.error(i18n.t('messages:tag_name_too_long', { defaultValue: 'Tag name is too long, please use 50 characters or less' }));
      return;
    }
    
    if (!isNoteOwner(currentSelectedNote, user)) {
      message.error(i18n.t('messages:no_permission_add_tag', { defaultValue: 'No permission: you can only add tags to your own notes' }));
      return;
    }
    
    try {
      // 检查标签是否已存在于当前笔记中
      const existingTag = currentSelectedNote.tags?.find(tag => tag.name === tagName);
      if (existingTag) {
        message.error(i18n.t('messages:tag_already_exists', { defaultValue: 'Tag already exists on this note' }));
        return;
      }

      // 先尝试创建或转换标签为自定义标签
      try {
        await apiClient.post('/notes/tags', {
          name: tagName,
          is_self_setup: true
        });
      } catch (tagError) {
        // 如果标签已经是自定义标签，继续执行；其他错误则抛出
        const errorMessage = tagError.response?.data?.message || tagError.message;
        if (errorMessage !== '标签已存在') {
          throw tagError;
        }
      }
      
      // 使用专门的添加标签API，而不是通用的更新API
      // 这样可以确保合并标签而不是替换，避免删除现有标签
      const addTagRes = await apiClient.post(`/notes/${currentSelectedNote.id}/tags`, {
        tags: [tagName] // 只传递新标签名称
      });
      
      if (!addTagRes.data) {
        message.error(i18n.t('messages:add_tag_failed_backend', { defaultValue: 'Failed to add tag: backend error' }));
      } else {
        // 用后端返回的最新数据（包含所有标签）
        setSelectedNote(addTagRes.data);
        message.success(i18n.t('messages:tag_added_success', { defaultValue: 'Tag added' }));
        // 关闭新增标签界面
        // setIsAddingTag function call removed - this should be passed as a parameter
        // setNewTagName function call removed - this should be passed as a parameter
        // 刷新历史标签列表和自定义标签列表
        await handleFetchUserHistoryTags();
        // fetchUserCustomTags function call removed - this should be passed as a parameter
      }
    } catch (error) {
      console.error('快速添加标签错误:', error);
      message.error(i18n.t('messages:add_tag_failed', { 
        defaultValue: 'Failed to add tag: {{error}}', 
        error: error.response?.data?.message || error.message 
      }));
      // 不需要回滚本地状态，因为使用专门的添加API不会影响现有标签
    }
  };

  // 添加新标签的处理函数
  const handleAddTag = async () => {
    const currentSelectedNote = getSelectedNote();
    console.log('[Debug] handleAddTag called:', {
      hasSelectedNote: !!currentSelectedNote,
      newTagName: newTagName,
      newTagNameType: typeof newTagName,
      trimmedLength: newTagName && typeof newTagName === 'string' ? newTagName.trim().length : 'N/A'
    });
    
    if (!currentSelectedNote) {
      message.error('Please select a note first');
      setNewTagName(''); // 清空输入框
      return;
    }
    
    if (!newTagName || typeof newTagName !== 'string' || !newTagName.trim()) {
      message.error(i18n.t('messages:please_enter_tag_name', { defaultValue: 'Please enter a tag name' }));
      setNewTagName(''); // 清空输入框
      return;
    }
    
    // 验证标签长度，数据库字段限制为50个字符
    if (newTagName.trim().length > 50) {
      message.error(i18n.t('messages:tag_name_too_long', { defaultValue: 'Tag name is too long, please use 50 characters or less' }));
      setNewTagName(''); // 清空输入框
      return;
    }
    
    if (!isNoteOwner(currentSelectedNote, user)) {
      message.error(i18n.t('messages:no_permission_add_tag', { defaultValue: 'No permission: you can only add tags to your own notes' }));
      setNewTagName(''); // 清空输入框
      return;
    }
    
    try {
      // 检查标签是否已存在于当前笔记中
      const existingTag = currentSelectedNote.tags?.find(tag => tag.name === newTagName.trim());
      if (existingTag) {
        message.error(i18n.t('messages:tag_already_exists', { defaultValue: 'Tag already exists on this note' }));
        setNewTagName(''); // 清空输入框
        return;
      }

      // 先尝试创建或转换标签为自定义标签
      try {
        await apiClient.post('/notes/tags', {
          name: newTagName.trim(),
          is_self_setup: true
        });
      } catch (tagError) {
        // 如果标签已经是自定义标签，继续执行；其他错误则抛出
        const errorMessage = tagError.response?.data?.message || tagError.message;
        if (errorMessage !== '标签已存在') {
          throw tagError;
        }
      }
      
      // 使用专门的添加标签API，而不是通用的更新API
      // 这样可以确保合并标签而不是替换，避免删除现有标签
      const addTagRes = await apiClient.post(`/notes/${currentSelectedNote.id}/tags`, {
        tags: [newTagName.trim()] // 只传递新标签名称
      });
      
      if (!addTagRes.data) {
        message.error(i18n.t('messages:add_tag_failed_backend', { defaultValue: 'Failed to add tag: backend error' }));
        setNewTagName(''); // 清空输入框
      } else {
        // 用后端返回的最新数据（包含所有标签）
        setSelectedNote(addTagRes.data);
        message.success(i18n.t('messages:tag_added_success', { defaultValue: 'Tag added' }));
        setNewTagName(''); // 清空输入框
        
        // 关闭历史列表和退出添加标签的编辑状态
        setIsAddingTag(false);
        setUserHistoryTags([]);
        
        // 退出笔记编辑状态（按用户需求：添加标签成功后退出编辑状态）
        setIsEditing(false);
        
        // 刷新历史标签列表和自定义标签列表
        await handleFetchUserHistoryTags();
        // fetchUserCustomTags function call removed - this should be passed as a parameter
      }
    } catch (error) {
      console.error('添加标签错误:', error);
      message.error(i18n.t('messages:add_tag_failed', { 
        defaultValue: 'Failed to add tag: {{error}}', 
        error: error.response?.data?.message || error.message 
      }));
      // 不需要回滚本地状态，因为使用专门的添加API不会影响现有标签
      setNewTagName(''); // 清空输入框
    }
  };

  // 添加自定义标签的处理函数
  const handleAddCustomTag = async (newCustomTagName) => {
    if (!newCustomTagName.trim()) {
      message.error(i18n.t('messages:please_enter_tag_name', { defaultValue: 'Please enter a tag name' }));
      return;
    }
    
    // 验证标签长度，数据库字段限制为50个字符
    if (newCustomTagName.trim().length > 50) {
      message.error(i18n.t('messages:tag_name_too_long', { defaultValue: 'Tag name is too long, please use 50 characters or less' }));
      return;
    }
    
    try {
      // 调用后端API创建自定义标签
      const response = await apiClient.post('/notes/tags', {
        name: newCustomTagName.trim(),
        is_self_setup: true
      });
      
      if (response.data) {
        message.success(i18n.t('messages:custom_tag_create_success', { defaultValue: 'Custom tag created successfully' }));
        
        // 刷新历史标签列表和自定义标签列表
        await handleFetchUserHistoryTags();
        // fetchUserCustomTags function call removed - this should be passed as a parameter
      }
    } catch (error) {
      console.error('创建自定义标签错误:', error);
      const errorMessage = error.response?.data?.message || error.message;
      if (errorMessage === '标签已存在') {
        message.error(i18n.t('messages:tag_already_custom', { defaultValue: 'This tag is already a custom tag' }));
      } else {
        message.error(i18n.t('messages:create_tag_failed', { 
          defaultValue: 'Failed to create tag: {{error}}', 
          error: errorMessage 
        }));
      }
    }
  };

  // 处理标签点击，筛选相关笔记
  const handleTagClick = (tag) => {
    const currentSelectedTag = getSelectedTag();
    if (currentSelectedTag && currentSelectedTag.id === tag.id) {
      // 如果点击的是已选中的标签，则取消筛选
      setSelectedTag(null);
    } else {
      // 选中新标签进行筛选
      setSelectedTag(tag);
    }
  };

  // 删除自定义标签的处理函数
  const handleDeleteCustomTag = async (tag) => {
    Modal.confirm({
      title: 'Confirm Delete Tag',
      content: `Are you sure you want to delete the tag "${tag.name}"? This will remove the tag from all associated notes.`,
      okText: 'Delete',
      cancelText: 'Cancel',
      okType: 'danger',
      onOk: async () => {
        try {
          const response = await apiClient.delete(`/notes/tags/${tag.id}`);
          if (response.data.success) {
            message.success(i18n.t('messages:tag_delete_success', { defaultValue: 'Tag deleted successfully' }));
            
            // 如果删除的是当前选中的标签，清除筛选
            const currentSelectedTag = getSelectedTag();
            if (currentSelectedTag && currentSelectedTag.id === tag.id) {
              setSelectedTag(null);
            }
            
            // 如果当前选中的笔记包含被删除的标签，需要更新笔记信息
            const currentSelectedNote = getSelectedNote();
            if (currentSelectedNote && currentSelectedNote.tags) {
              const hasDeletedTag = currentSelectedNote.tags.some(t => t.id === tag.id);
              if (hasDeletedTag) {
                // 从当前选中笔记中移除被删除的标签
                const updatedTags = currentSelectedNote.tags.filter(t => t.id !== tag.id);
                setSelectedNote(prev => ({ ...prev, tags: updatedTags }));
              }
            }
            
            // 刷新自定义标签列表和历史标签列表
            // fetchUserCustomTags function call removed - this should be passed as a parameter
            await handleFetchUserHistoryTags();
            
            // 重新获取笔记数据以确保所有笔记的标签信息都是最新的
            // fetchNotes function call removed - this should be passed as a parameter
            
            // 如果有选中的笔记，重新加载该笔记的最新数据
            if (currentSelectedNote) {
              try {
                const noteResponse = await apiClient.get(`/notes/${currentSelectedNote.id}`);
                if (noteResponse.data) {
                  setSelectedNote(noteResponse.data);
                }
              } catch (noteError) {
                console.error('重新加载笔记详情失败:', noteError);
              }
            }
          }
        } catch (error) {
          console.error('删除标签错误:', error);
          message.error(i18n.t('messages:delete_tag_failed', { 
            defaultValue: 'Failed to delete tag: {{error}}', 
            error: error.response?.data?.message || error.message 
          }));
        }
      }
    });
  };

  // 处理使用主题模板功能
  const handleUseTopicTemplate = async () => {
    console.log('[handleUseTopicTemplate] 开始处理使用主题模板功能');
    
    try {
      // 检查用户是否已有私有知识库
      console.log('[handleUseTopicTemplate] 发送API请求检查私有库状态');
      console.log('[handleUseTopicTemplate] API URL: /users/knowledge-base/private-status');
      
      const response = await apiClient.get('/users/knowledge-base/private-status');
      console.log('[handleUseTopicTemplate] API响应:', response);
      console.log('[handleUseTopicTemplate] 响应数据:', response.data);
      
      const hasPrivateKB = response.data?.data?.hasPrivateKB;
      console.log('[handleUseTopicTemplate] 私有库状态:', hasPrivateKB ? '已存在' : '不存在');

      if (hasPrivateKB) {
        // 已有私有库，显示提示
        console.log('[handleUseTopicTemplate] 用户已有私有库，显示提示信息');
        Modal.info({
          title: 'Private KB Status',
          content: 'Private KB has already been initialized',
          okText: 'OK'
        });
      } else {
        // 没有私有库，显示确认对话框
        console.log('[handleUseTopicTemplate] 用户没有私有库，显示初始化确认对话框');
        Modal.confirm({
          title: 'Initialize Private KB',
          content: 'Confirm initialization of the private knowledge base?',
          okText: 'Confirm',
          cancelText: 'Cancel',
          onOk: async () => {
            console.log('[handleUseTopicTemplate] 用户确认初始化私有库');
            try {
              console.log('[handleUseTopicTemplate] 发送初始化API请求');
              console.log('[handleUseTopicTemplate] API URL: /users/knowledge-base/private/initialize');
              
              const initResponse = await apiClient.post('/users/knowledge-base/private/initialize');
              console.log('[handleUseTopicTemplate] 初始化API响应:', initResponse);
              console.log('[handleUseTopicTemplate] 初始化成功，知识库数据:', initResponse.data);
              
              message.success(i18n.t('messages:private_kb_init_success', { defaultValue: 'Private KB initialized successfully!' }));
            } catch (error) {
              console.error('[handleUseTopicTemplate] 初始化私有库失败:', error);
              console.error('[handleUseTopicTemplate] 错误详情:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                statusText: error.response?.statusText
              });
              message.error(i18n.t('messages:private_kb_init_failed', { 
                defaultValue: 'Failed to initialize private KB: {{error}}', 
                error: error.response?.data?.message || error.message 
              }));
            }
          },
          onCancel: () => {
            console.log('[handleUseTopicTemplate] 用户取消了初始化操作');
          }
        });
      }
    } catch (error) {
      console.error('[handleUseTopicTemplate] 检查私有库状态失败:', error);
      console.error('[handleUseTopicTemplate] 错误详情:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        stack: error.stack
      });
      message.error(i18n.t('messages:check_private_kb_status_failed', { 
        defaultValue: 'Failed to check private KB status: {{error}}', 
        error: error.response?.data?.message || error.message 
      }));
    }
  };

  return {
    handleLogout,
    handleEdit,
    handleSave,
    handlePin,
    handleTogglePublic,
    handleCancel,
    handleDeleteNote,
    startCreateTimer,
    handleCreateFromLink,
    handleCreateModalCancel,
    handleFileUpload,
    handleFetchUserHistoryTags,
    handleQuickAddTag,
    handleAddTag,
    handleAddCustomTag,
    handleTagClick,
    handleDeleteCustomTag,
    handleUseTopicTemplate,
    togglePinNote
  };
};

// 协作库处理函数工厂
export const createCollaborativeHandlers = (deps) => {
  const {
    setIsCreateCollaborativeVisible,
    createCollaborativeForm,
    fetchUserCollaborativeKBs,
    refreshKnowledgeBasesAndTopics
  } = deps;

  // 创建协作库
  const handleCreateCollaborative = async (values) => {
    try {
      const result = await apiHelpers.createCollaborativeKB({
        name: values.name,
        description: values.description
      });
      
      message.success(i18n.t('messages:collaborative_kb_create_success', { defaultValue: 'Collaborative KB created successfully!' }));
      setIsCreateCollaborativeVisible(false);
      createCollaborativeForm.resetFields();
      
      // 刷新知识库和主题数据
      if (refreshKnowledgeBasesAndTopics) {
        await refreshKnowledgeBasesAndTopics();
      } else if (fetchUserCollaborativeKBs) {
        // 回退到原来的方法
        await fetchUserCollaborativeKBs();
      }
      
      return result;
    } catch (error) {
      console.error('创建协作库错误:', error);
      message.error(i18n.t('messages:collaborative_kb_create_failed', { 
        defaultValue: 'Failed to create collaborative KB: {{error}}', 
        error: error.response?.data?.message || error.message 
      }));
    }
  };

  // 取消创建协作库
  const handleCancelCreateCollaborative = () => {
    createCollaborativeForm.resetFields();
    setIsCreateCollaborativeVisible(false);
  };

  return {
    handleCreateCollaborative,
    handleCancelCreateCollaborative
  };
};

// 数据处理和计算函数
export const calculateCounts = (allNotes, privateTopics = []) => {
  // 计算私有库笔记数量（属于私有知识库的笔记）
  const privateNotesCount = allNotes.filter(note => {
    const noteTopicIds = note.topicMappings ? note.topicMappings.map(mapping => mapping.topic_id) : [];
    return noteTopicIds.length > 0 && noteTopicIds.some(topicId => isTopicInPrivateKB(topicId, privateTopics));
  }).length;

  // 计算协作库笔记数量（有主题但不属于私有库主题的笔记）
  const sharedNotesCount = allNotes.filter(note => {
    const noteTopicIds = note.topicMappings ? note.topicMappings.map(mapping => mapping.topic_id) : [];
    if (noteTopicIds.length === 0) return false;
    const isPrivateNote = noteTopicIds.some(topicId => isTopicInPrivateKB(topicId, privateTopics));
    return !isPrivateNote;
  }).length;

  // 计算最近一周编辑的笔记数量
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  const recentNotesCount = allNotes.filter(note => {
    const updateTime = new Date(note.updated_at || note.created_at);
    return updateTime >= oneWeekAgo;
  }).length;

  console.log('[前端] 笔记数量统计:', {
    总笔记数: allNotes.length,
    私有库笔记数: privateNotesCount,
    协作库笔记数: sharedNotesCount,
    最近一周笔记数: recentNotesCount,
    私有主题数量: privateTopics?.length || 0
  });

  return {
    sharedNotesCount,
    privateNotesCount,
    recentNotesCount,
    oneWeekAgo
  };
};

// 笔记排序函数
export const sortNotes = (allNotes, selectedFolder) => {
  return selectedFolder === 'recent' 
    ? allNotes.sort((a, b) => {
        // 最近编辑视图：严格按照更新时间排序
        const timeA = new Date(a.updated_at || a.created_at);
        const timeB = new Date(b.updated_at || b.created_at);
        return timeB - timeA;
      })
    : allNotes.sort((a, b) => {
        // 其他视图：置顶优先，然后按时间排序
        if (a.is_pinned !== b.is_pinned) {
          return b.is_pinned - a.is_pinned;
        }
        const timeA = new Date(a.updated_at || a.created_at);
        const timeB = new Date(b.updated_at || b.created_at);
        return timeB - timeA;
      });
};

// 笔记筛选函数
export const filterNotes = (sortedNotes, { searchKeyword, selectedFolder, selectedTag, selectedTopic, selectedKB, oneWeekAgo, privateTopics, collaborativeKBTopics = {}, allKnowledgeBases = [] }) => {
  console.log('[前端] 开始筛选笔记:', {
    totalNotes: sortedNotes.length,
    selectedFolder,
    searchKeyword,
    selectedTag: selectedTag?.name,
    selectedTopic: selectedTopic?.name,
    selectedKB: selectedKB?.name,
    privateTopicsCount: privateTopics?.length || 0,
    timestamp: new Date().toISOString()
  });

  const filteredResults = sortedNotes.filter(note => {
    // 扩展的关键词匹配逻辑
    const matchKeyword = searchKeyword === '' || (() => {
      const keyword = searchKeyword.toLowerCase();
      
      // 1. 搜索笔记标题
      const titleMatch = note.title.toLowerCase().includes(keyword);
      
      // 2. 搜索笔记内容
      const contentMatch = note.content && note.content.toLowerCase().includes(keyword);
      
      // 3. 搜索笔记摘要
      const summaryMatch = note.summary && note.summary.toLowerCase().includes(keyword);
      
      // 4. 搜索笔记标签
      const tagsMatch = note.tags && note.tags.some(tag => 
        tag.name.toLowerCase().includes(keyword)
      );
      
      // 5. 搜索笔记所属主题（多对多关系）
      const topicMatch = (() => {
        if (!note.topicMappings || note.topicMappings.length === 0) return false;
        
        // 检查笔记的每个主题
        for (const mapping of note.topicMappings) {
          const topicId = mapping.topic_id;
          
          // 搜索私有库主题
          const privateTopicMatch = searchInTopicTree(topicId, privateTopics, keyword);
          if (privateTopicMatch) return true;
          
          // 搜索协作库主题
          for (const kbId in collaborativeKBTopics) {
            const topics = collaborativeKBTopics[kbId];
            if (searchInTopicTree(topicId, topics, keyword)) {
              return true;
            }
          }
        }
        
        return false;
      })();
      
      // 只在有搜索关键词且匹配时才显示详细日志
      if (keyword && (titleMatch || contentMatch || summaryMatch || tagsMatch || topicMatch)) {
        console.log(`[前端] 搜索匹配详情 [${note.title.substring(0, 20)}]:`, {
          keyword,
          titleMatch,
          contentMatch,
          summaryMatch,
          tagsMatch,
          topicMatch,
          finalMatch: titleMatch || contentMatch || summaryMatch || tagsMatch || topicMatch
        });
      }
      
      return titleMatch || contentMatch || summaryMatch || tagsMatch || topicMatch;
    })();
    
    // 获取笔记的主题ID列表（多对多关系）
    const noteTopicIds = note.topicMappings ? note.topicMappings.map(mapping => mapping.topic_id) : [];
    
    // 检查笔记是否属于私有库（通过topicMappings判断）
    const isPrivateNote = noteTopicIds.length > 0 && privateTopics && 
      noteTopicIds.some(topicId => isTopicInPrivateKB(topicId, privateTopics));
    
    // 检查笔记是否属于协作库（有主题但不属于私有库主题）
    const isCollaborativeNote = noteTopicIds.length > 0 && !isPrivateNote;
    
    // 检查笔记的知识库是否为私有库（用于文件夹级别的筛选）
    const belongsToPrivateKB = note.knowledgeBase && note.knowledgeBase.is_private === true;
    
    const matchFolder = selectedFolder === 'all' || 
      (selectedFolder === 'starred' && note.is_pinned) ||
      (selectedFolder === 'pinned' && note.is_pinned) ||
      (selectedFolder === 'recent' && (() => {
        const updateTime = new Date(note.updated_at || note.created_at);
        return updateTime >= oneWeekAgo;
      })()) ||
      (selectedFolder === 'private' && belongsToPrivateKB) ||
      (selectedFolder === 'collaborative' && !belongsToPrivateKB && note.knowledgeBase) ||
      (selectedFolder === 'topic' && selectedTopic) ||
      (selectedFolder === 'kb' && (() => {
        // 知识库筛选：检查笔记是否属于选中的知识库
        if (!selectedKB) {
          console.log('[前端] 知识库筛选：没有选中知识库，显示所有笔记');
          return true;
        }
        
        const selectedKbId = selectedKB.kb_id;
        
        // 优先检查笔记的 kbId（笔记对象上可能有 kb_id 或 knowledgeBase.kb_id）
        const noteKbId = note.kb_id || (note.knowledgeBase && note.knowledgeBase.kb_id);
        
        if (noteKbId) {
          // 如果笔记有 kbId，直接比较 kbId 是否匹配
          // 这种情况下不需要检查 topicId，因为 kbId 已经足够确定笔记属于哪个知识库
          const matches = noteKbId === selectedKbId;
          console.log('[前端] 知识库筛选（通过kbId）:', {
            noteId: note.id,
            noteTitle: note.title.substring(0, 30),
            selectedKB: selectedKB.name,
            selectedKbId: selectedKbId,
            noteKbId: noteKbId,
            matches: matches
          });
          return matches;
        }
        
        // 如果笔记没有 kbId，但有 topicId，则作为回退方案
        // 因为后端已经按 kbId 过滤了，如果笔记没有 kbId 但有 topicId，说明可能属于该知识库
        if (noteTopicIds.length > 0) {
          console.log('[前端] 知识库筛选（通过topicId回退）:', {
            noteId: note.id,
            noteTitle: note.title.substring(0, 30),
            selectedKB: selectedKB.name,
            selectedKbId: selectedKbId,
            noteTopicIds: noteTopicIds,
            noteKbId: noteKbId || '无'
          });
          // 后端已经按 kbId 过滤了，如果有主题关联，认为属于该知识库
          return true;
        }
        
        // 既没有 kbId 也没有 topicId，不显示
        console.log('[前端] 知识库筛选：笔记既没有kbId也没有topicId，不显示');
        return false;
      })()) ||
      (selectedFolder === 'trash' /* TODO: Implement trash logic based on actual data */);
    
    // 标签筛选：如果选中了标签，只显示包含该标签的笔记
    const matchTag = !selectedTag || (note.tags && note.tags.some(tag => tag.name === selectedTag.name));
    
    // 主题筛选：如果选中了主题，显示该主题及其子主题的笔记（多对多关系）
    const matchTopic = !selectedTopic || (noteTopicIds.length > 0 && 
      noteTopicIds.some(topicId => isTopicOrSubtopic(topicId, selectedTopic)));
    
    const matches = matchKeyword && matchFolder && matchTag && matchTopic;
    
    // 为特定文件夹类型添加详细日志
    if (selectedFolder === 'private' || selectedFolder === 'collaborative') {
      console.log(`[前端] 笔记筛选详情 [${selectedFolder}]:`, {
        noteId: note.id,
        noteTitle: note.title.substring(0, 30),
        topicIds: noteTopicIds,
        isPrivateNote: isPrivateNote,
        isCollaborativeNote: isCollaborativeNote,
        belongsToPrivateKB: belongsToPrivateKB,
        knowledgeBase: note.knowledgeBase ? { kb_id: note.knowledgeBase.kb_id, is_private: note.knowledgeBase.is_private } : null,
        matchKeyword,
        matchFolder,
        matchTag,
        matchTopic,
        finalMatch: matches
      });
    }
    
    return matches;
  });

  // 如果有搜索关键词，统计各类型匹配的数量
  let searchStats = null;
  if (searchKeyword) {
    searchStats = {
      titleMatches: 0,
      contentMatches: 0,
      summaryMatches: 0,
      tagMatches: 0,
      topicMatches: 0
    };
    
    filteredResults.forEach(note => {
      const keyword = searchKeyword.toLowerCase();
      if (note.title.toLowerCase().includes(keyword)) searchStats.titleMatches++;
      if (note.content && note.content.toLowerCase().includes(keyword)) searchStats.contentMatches++;
      if (note.summary && note.summary.toLowerCase().includes(keyword)) searchStats.summaryMatches++;
      if (note.tags && note.tags.some(tag => tag.name.toLowerCase().includes(keyword))) searchStats.tagMatches++;
      // 主题匹配检查较复杂，这里简化处理
    });
  }

  console.log('[前端] 筛选完成:', {
    originalCount: sortedNotes.length,
    filteredCount: filteredResults.length,
    selectedFolder,
    searchKeyword,
    searchStats,
    timestamp: new Date().toISOString()
  });

  return filteredResults;
};

// 辅助函数：在主题树中搜索关键词
const searchInTopicTree = (noteTopicId, topics, keyword) => {
  if (!topics || !Array.isArray(topics)) return false;
  
  const searchTopic = (topicList) => {
    for (const topic of topicList) {
      // 如果笔记属于这个主题，检查主题名称是否匹配关键词
      if (topic.topic_id === noteTopicId) {
        return topic.name.toLowerCase().includes(keyword);
      }
      
      // 递归搜索子主题
      if (topic.children && topic.children.length > 0) {
        if (searchTopic(topic.children)) {
          return true;
        }
      }
    }
    return false;
  };
  
  return searchTopic(topics);
};

// 辅助函数：检查主题ID是否属于私有库主题树
const isTopicInPrivateKB = (topicId, privateTopics) => {
  if (!topicId || !privateTopics || privateTopics.length === 0) return false;
  
  // 递归检查主题树
  const checkTopic = (topics) => {
    for (const topic of topics) {
      if (topic.topic_id === topicId) {
        return true;
      }
      if (topic.children && topic.children.length > 0) {
        if (checkTopic(topic.children)) {
          return true;
        }
      }
    }
    return false;
  };
  
  return checkTopic(privateTopics);
};

// 辅助函数：检查主题ID是否属于协作库主题树
const isTopicInCollaborativeKB = (topicId, collaborativeTopics) => {
  if (!topicId || !collaborativeTopics || collaborativeTopics.length === 0) return false;
  
  // 递归检查主题树
  const checkTopic = (topics) => {
    for (const topic of topics) {
      if (topic.topic_id === topicId) {
        return true;
      }
      if (topic.children && topic.children.length > 0) {
        if (checkTopic(topic.children)) {
          return true;
        }
      }
    }
    return false;
  };
  
  return checkTopic(collaborativeTopics);
};

// 辅助函数：检查笔记的主题是否为选中主题或其子主题
const isTopicOrSubtopic = (noteTopicId, selectedTopic) => {
  if (!noteTopicId || !selectedTopic) return false;
  
  // 如果笔记直接属于选中的主题
  if (noteTopicId === selectedTopic.topic_id) {
    return true;
  }
  
  // 如果笔记属于选中主题的子主题
  if (selectedTopic.children && selectedTopic.children.length > 0) {
    return selectedTopic.children.some(child => 
      noteTopicId === child.topic_id || isTopicOrSubtopic(noteTopicId, child)
    );
  }
  
  return false;
};

// 快捷操作数据
export const getQuickActions = (statsData) => {
  // 如果没有统计数据，返回默认值
  if (!statsData) {
    return [
      { key: 'all', title: 'All Notes', description: '0 notes', count: 0, icon: 'FolderOutlined', isPrivate: false },
      { key: 'pinned', title: 'Pinned Notes', description: '0 notes', count: 0, icon: 'StarOutlined', isPrivate: false },
      { key: 'recent', title: 'Recent Notes (7 days)', description: '0 notes', count: 0, icon: 'ClockCircleOutlined', isPrivate: false }
    ];
  }

  return [
    {
      key: 'all',
      title: 'All Notes',
      description: `${statsData.totalNotes || 0} notes`,
      count: statsData.totalNotes || 0,
      icon: 'FolderOutlined',
      isPrivate: false
    },
    {
      key: 'pinned',
      title: 'Pinned Notes',
      description: `${statsData.pinnedNotes || 0} notes`,
      count: statsData.pinnedNotes || 0,
      icon: 'StarOutlined',
      isPrivate: false
    },
    {
      key: 'recent',
      title: 'Recent Notes (7 days)',
      description: `${statsData.recentNotes || 0} notes`,
      count: statsData.recentNotes || 0,
      icon: 'ClockCircleOutlined',
      isPrivate: false
    }
  ];
};

// 主题树工具函数
export const topicTreeHelpers = {
  // 递归更新主题树中的主题
  updateTopicInTree: (topics, targetId, updates) => {
    return topics.map(topic => {
      if (topic.topic_id === targetId) {
        return { ...topic, ...updates };
      }
      if (topic.children && topic.children.length > 0) {
        return {
          ...topic,
          children: topicTreeHelpers.updateTopicInTree(topic.children, targetId, updates)
        };
      }
      return topic;
    });
  },

  // 递归从主题树中移除主题
  removeTopicFromTree: (topics, targetId) => {
    return topics.filter(topic => {
      if (topic.topic_id === targetId) {
        return false;
      }
      if (topic.children && topic.children.length > 0) {
        topic.children = topicTreeHelpers.removeTopicFromTree(topic.children, targetId);
      }
      return true;
    });
  }
};

// API调用函数
export const apiHelpers = {
  // 获取用户自定义标签
  fetchUserCustomTags: async () => {
    try {
      const response = await apiClient.get('/notes/user/tags');
      return response.data || [];
    } catch (error) {
      console.error('获取用户自定义标签失败:', error);
      return [];
    }
  },

  // 获取私有知识库主题树
  fetchPrivateKBTopics: async () => {
    try {
      const response = await apiClient.get('/users/knowledge-base/private/topics');
      console.log('私有知识库API响应:', response.data);
      
      // 检查响应数据结构
      const data = response.data?.data || response.data;
      
      if (data && data.knowledgeBase) {
        return {
          hasPrivateKB: true,
          topics: data.topics || [],
          knowledgeBase: data.knowledgeBase // 确保返回知识库信息
        };
      } else {
        return {
          hasPrivateKB: false,
          topics: [],
          knowledgeBase: null
        };
      }
    } catch (error) {
      console.error('获取私有知识库主题失败:', error);
      return {
        hasPrivateKB: false,
        topics: [],
        knowledgeBase: null
      };
    }
  },

  // 获取用户所有知识库（通过user_kb_mapping表）
  fetchUserAllKnowledgeBases: async () => {
    try {
      const response = await apiClient.get('/users/knowledge-bases');
      return response.data?.data || [];
    } catch (error) {
      console.error('获取用户知识库列表失败:', error);
      return [];
    }
  },

  // 创建多人协作库
  createCollaborativeKB: async (data) => {
    try {
      const response = await apiClient.post('/users/knowledge-base/collaborative', data);
      return response.data;
    } catch (error) {
      console.error('创建协作库失败:', error);
      throw error;
    }
  },

  // 获取用户协作库列表
  fetchUserCollaborativeKBs: async () => {
    try {
      const response = await apiClient.get('/users/knowledge-base/collaborative');
      return response.data?.data || [];
    } catch (error) {
      console.error('获取协作库列表失败:', error);
      return [];
    }
  },

  // 获取协作库主题树
  fetchCollaborativeKBTopics: async (kbId) => {
    try {
      const response = await apiClient.get(`/users/knowledge-base/collaborative/${kbId}/topics`);
      return response.data?.data || [];
    } catch (error) {
      console.error('获取协作库主题树失败:', error);
      return [];
    }
  },

  /**
   * 创建协作库主题API
   * 功能：调用后端API创建协作库主题
   * 参数：kbId - 协作库ID, data - 主题数据对象
   * 修改时间：2025.10.19
   * 修改内容：配合前端防多次点击保护机制，确保API调用的稳定性
   */
  createCollaborativeKBTopic: async (kbId, data) => {
    try {
      const response = await apiClient.post(`/users/knowledge-base/collaborative/${kbId}/topics`, data);
      return response.data;
    } catch (error) {
      console.error('创建协作库主题失败:', error);
      throw error;
    }
  },

  /**
   * 删除协作库主题API
   * 功能：调用后端API删除协作库主题
   * 参数：kbId - 协作库ID, topicId - 主题ID
   * 修改时间：2025.10.19
   * 修改内容：
   * 1. 添加详细的调试日志记录
   * 2. 优化错误处理和日志输出
   * 3. 配合前端防多次点击保护机制
   */
  deleteCollaborativeKBTopic: async (kbId, topicId) => {
    try {
      console.log('[deleteCollaborativeKBTopic] 开始调用删除API:', { kbId, topicId });
      console.log('[deleteCollaborativeKBTopic] apiClient对象:', typeof apiClient, apiClient);
      console.log('[deleteCollaborativeKBTopic] 构建请求URL:', `/users/knowledge-base/collaborative/${kbId}/topics/${topicId}`);
      
      const response = await apiClient.delete(`/users/knowledge-base/collaborative/${kbId}/topics/${topicId}`);
      console.log('[deleteCollaborativeKBTopic] API调用成功:', response.data);
      return response.data;
    } catch (error) {
      console.error('[deleteCollaborativeKBTopic] 删除协作库主题失败:', error);
      console.error('[deleteCollaborativeKBTopic] 错误详情:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        stack: error.stack
      });
      throw error;
    }
  },

  // 更新协作库主题
  updateCollaborativeKBTopic: async (kbId, topicId, data) => {
    try {
      const response = await apiClient.put(`/users/knowledge-base/collaborative/${kbId}/topics/${topicId}`, data);
      return response.data;
    } catch (error) {
      console.error('更新协作库主题失败:', error);
      throw error;
    }
  },

  // 获取协作库成员列表
  fetchCollaborativeKBMembers: async (kbId) => {
    try {
      const response = await apiClient.get(`/users/knowledge-base/collaborative/${kbId}/members`);
      return response.data?.data || [];
    } catch (error) {
      console.error('获取协作库成员失败:', error);
      throw error;
    }
  },

  // 获取笔记统计数据
  fetchNotesStats: async () => {
    try {
      console.log('[前端] 开始获取笔记统计数据...');
      const response = await apiClient.get('/notes/stats');
      console.log('[前端] 获取笔记统计数据成功:', response.data);
      
      // 确保返回的数据格式正确
      if (!response.data) {
        throw new Error('Server returned empty notes statistics data');
      }
      
      // 确保所有必要的字段都存在
      const stats = {
        totalNotes: response.data.totalNotes || 0,
        privateNotes: response.data.privateNotes || 0,
        collaborativeNotes: response.data.collaborativeNotes || 0,
        pinnedNotes: response.data.pinnedNotes || 0,
        recentNotes: response.data.recentNotes || 0,
        publicNotes: response.data.publicNotes || 0
      };
      
      console.log('[前端] 处理后的笔记统计数据:', stats);
      return stats;
      
    } catch (error) {
      console.error('[前端] 获取笔记统计数据失败:', error);
      console.error('[前端] 错误详情:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error; // 向上抛出错误，让调用者处理
    }
  },

  // 获取主题的笔记数量
  fetchTopicNotesCount: async (topicId) => {
    try {
      console.log(`[前端] 开始获取主题 ${topicId} 的笔记数量...`);
      const response = await apiClient.get(`/notes/topic/${topicId}/count`);
      console.log(`[前端] 获取主题 ${topicId} 笔记数量成功:`, response.data);
      
      return response.data?.count || 0;
      
    } catch (error) {
      console.error(`[前端] 获取主题 ${topicId} 笔记数量失败:`, error);
      return 0; // 失败时返回0
    }
  },

  // 获取知识库的笔记数量
  fetchKbNotesCount: async (kbId) => {
    try {
      console.log(`[前端] 开始获取知识库 ${kbId} 的笔记数量...`);
      const response = await apiClient.get(`/notes/kb/${kbId}/count`);
      console.log(`[前端] 获取知识库 ${kbId} 笔记数量成功:`, response.data);
      
      return response.data?.count || 0;
      
    } catch (error) {
      console.error(`[前端] 获取知识库 ${kbId} 笔记数量失败:`, error);
      return 0; // 失败时返回0
    }
  },

  // 批量获取多个主题的笔记数量（使用新的批量API）
  fetchMultipleTopicNotesCount: async (topicIds) => {
    try {
      console.log(`[前端] 开始批量获取 ${topicIds.length} 个主题的笔记数量...`);
      
      // 使用新的批量API
      const response = await apiClient.post('/notes/topics/count', {
        topicIds: topicIds
      });
      
      console.log(`[前端] 批量获取主题笔记数量成功:`, response.data);
      return response.data || {};
      
    } catch (error) {
      console.error(`[前端] 批量获取主题笔记数量失败:`, error);
      return {};
    }
  },

  // 初始化私有知识库
  initializePrivateKB: async () => {
    try {
      console.log('[前端] 开始初始化私有知识库...');
      const response = await apiClient.post('/users/knowledge-base/private/initialize');
      console.log('[前端] 初始化私有知识库成功:', response.data);
      return response.data;
    } catch (error) {
      console.error('[前端] 初始化私有知识库失败:', error);
      throw error;
    }
  }
};

// 主题相关的处理函数工厂
export const createTopicHandlers = (deps) => {
  const {
    setExpandedTopics,
    setEditingTopic,
    setEditingTopicName,
    setEditingTopicContext,
    setPrivateTopics,
    fetchPrivateKBTopics,
    setIsCreateTopicVisible,
    createTopicForm,
    setIsCreateSubTopicVisible,
    createSubTopicForm,
    setSelectedParentTopic,
    setCollaborativeKBTopics,
    refreshKnowledgeBasesAndTopics
  } = deps;

  // 切换主题展开/折叠状态
  const toggleTopicExpanded = (topicId) => {
    console.log('[DashboardUtils] toggleTopicExpanded 被调用:', topicId);
    setExpandedTopics(prev => {
      // 确保 prev 是数组
      const currentArray = Array.isArray(prev) ? prev : [];
      console.log('[DashboardUtils] 当前展开状态:', currentArray);
      
      if (currentArray.includes(topicId)) {
        const newArray = currentArray.filter(id => id !== topicId);
        console.log('[DashboardUtils] 收起主题，新状态:', newArray);
        return newArray;
      } else {
        const newArray = [...currentArray, topicId];
        console.log('[DashboardUtils] 展开主题，新状态:', newArray);
        return newArray;
      }
    });
  };

  // 开始重命名主题
  const handleRenameTopic = (topic, context) => {
    setEditingTopic(topic.topic_id);
    setEditingTopicName(topic.name);
    setEditingTopicContext(context); // 保存上下文信息
  };

  // 确认重命名主题
  const handleConfirmRename = async (topicId, editingTopicName, editingTopicContext) => {
    try {
      if (editingTopicContext && editingTopicContext.type === 'collaborative') {
        // 协作库主题：调用协作库更新API
        const response = await apiClient.put(`/users/knowledge-base/collaborative/${editingTopicContext.kbId}/topics/${topicId}`, {
          name: editingTopicName
        });
        
        console.log('重命名协作库主题成功:', response.data);
        message.success('Collaborative KB topic renamed successfully');
        
        // 刷新该协作库的主题树
        const topics = await apiHelpers.fetchCollaborativeKBTopics(editingTopicContext.kbId);
        setCollaborativeKBTopics(prev => ({
          ...prev,
          [editingTopicContext.kbId]: topics
        }));
      } else {
        // 私有库主题：使用原有逻辑
        const response = await apiClient.put(`/users/knowledge-base/private/topics/${topicId}`, {
          name: editingTopicName
        });
        
        console.log('重命名私有库主题成功:', response.data);
        message.success('Topic renamed successfully');
        
        // 更新本地状态
        setPrivateTopics(prev => topicTreeHelpers.updateTopicInTree(prev, topicId, { name: editingTopicName }));
      }
      
      setEditingTopic(null);
      setEditingTopicName('');
      setEditingTopicContext(null);
    } catch (error) {
      console.error('重命名主题失败:', error);
      message.error('Rename failed: ' + (error.response?.data?.message || error.message));
    }
  };

  // 取消重命名
  const handleCancelRename = () => {
    setEditingTopic(null);
    setEditingTopicName('');
    setEditingTopicContext(null);
  };

  // 删除主题
  const handleDeleteTopic = async (topicOrTopicId, context = null) => {
    try {
      // 处理参数：可能是topic对象或topicId字符串
      let topicId, topicName;
      if (typeof topicOrTopicId === 'string') {
        topicId = topicOrTopicId;
        topicName = i18n.t('dashboard:default_topic_name', { defaultValue: 'this topic' }); // 如果没有topic对象，使用默认名称
      } else {
        topicId = topicOrTopicId.topic_id;
        topicName = topicOrTopicId.name;
      }
      
      // 显示确认对话框
      console.log('[handleDeleteTopic] 准备显示确认对话框');
      const confirmed = window.confirm(i18n.t('dashboard:confirm_delete_topic', { 
        defaultValue: 'Are you sure you want to delete topic "{{topicName}}"? This action cannot be undone.', 
        topicName 
      }));
      console.log('[handleDeleteTopic] window.confirm结果:', confirmed);
      
      if (!confirmed) {
        console.log('[handleDeleteTopic] 用户取消删除');
        return; // 直接返回，不执行删除操作
      }
      
      console.log('[handleDeleteTopic] 用户确认删除，开始调用API');
      
      // 调用后端API删除主题
      const response = await apiClient.delete(`/users/knowledge-base/private/topics/${topicId}`);
      
      console.log('删除主题成功:', response.data);
      message.success(i18n.t('messages:topic_delete_success', { defaultValue: 'Topic deleted successfully' }));
      
      // 刷新知识库和主题数据
      if (refreshKnowledgeBasesAndTopics) {
        await refreshKnowledgeBasesAndTopics();
      } else {
        // 回退到原来的方法
        await fetchPrivateKBTopics();
      }
    } catch (error) {
      console.error('删除主题失败:', error);
      const errorMessage = error.response?.data?.message || error.message;
      setTimeout(() => {
        alert(i18n.t('messages:delete_topic_failed', { 
          defaultValue: 'Failed to delete topic: {{error}}', 
          error: errorMessage 
        }));
      }, 100);
      //message.error('删除失败: ' + errorMessage);
    }
  };

  // 创建新主题（level 0 根级主题）
  const handleCreateTopic = async (values) => {
    try {
      const response = await apiClient.post('/users/knowledge-base/private/topics', {
        name: values.name,
        description: values.description,
        tagnamelist: values.tagnamelist,
        parentId: null // 始终创建根级主题（level 0）
      });
      
      console.log('创建主题成功:', response.data);
      message.success(i18n.t('messages:topic_create_success', { defaultValue: 'Topic created successfully' }));
      
      // 刷新知识库和主题数据
      if (refreshKnowledgeBasesAndTopics) {
        await refreshKnowledgeBasesAndTopics();
      } else {
        // 回退到原来的方法
        await fetchPrivateKBTopics();
      }
      
      // 重置表单并关闭模态框
      createTopicForm.resetFields();
      setIsCreateTopicVisible(false);
    } catch (error) {
      console.error('创建主题失败:', error);
      message.error(i18n.t('messages:topic_create_failed', { 
        defaultValue: 'Creation failed: {{error}}', 
        error: error.response?.data?.message || error.message 
      }));
    }
  };

  // 取消创建主题
  const handleCancelCreateTopic = () => {
    createTopicForm.resetFields();
    setIsCreateTopicVisible(false);
  };

  // 创建子主题
  const handleCreateSubTopic = async (values, selectedParentTopic) => {
    try {
      // 检查是否是协作库的主题
      const isCollaborative = selectedParentTopic.context && selectedParentTopic.context.type === 'collaborative';
      
      if (isCollaborative) {
        // 协作库子主题创建
        const response = await apiClient.post(`/users/knowledge-base/collaborative/${selectedParentTopic.context.kbId}/topics`, {
          name: values.name,
          description: values.description,
          tagnamelist: values.tagnamelist,
          parentId: selectedParentTopic.topic_id // 设置父主题ID
        });
        
        console.log('创建协作库子主题成功:', response.data);
        message.success(`Sub-topic created successfully under "${selectedParentTopic.name}"`);

        // 强制清除当前协作库的主题数据缓存，确保获取最新数据
        if (setCollaborativeKBTopics && selectedParentTopic.context && selectedParentTopic.context.kbId) {
          const kbId = selectedParentTopic.context.kbId;
          console.log('[handleCreateSubTopic] 强制清除协作库主题数据缓存:', kbId);
          setCollaborativeKBTopics(prev => {
            const newTopics = { ...prev };
            delete newTopics[kbId]; // 清除缓存
            return newTopics;
          });
          
          // 重新获取协作库主题数据以确保数据同步
          try {
            console.log('[handleCreateSubTopic] 重新获取协作库主题数据:', kbId);
            const updatedTopics = await apiHelpers.fetchCollaborativeKBTopics(kbId);
            setCollaborativeKBTopics(prev => {
              const newTopics = {
                ...prev,
                [kbId]: updatedTopics
              };
              console.log('[handleCreateSubTopic] 更新协作库主题数据:', {
                kbId: kbId,
                oldTopics: prev[kbId]?.length || 0,
                newTopics: updatedTopics?.length || 0
              });
              return newTopics;
            });
          } catch (refreshError) {
            console.error('[handleCreateSubTopic] 重新获取协作库主题数据失败:', refreshError);
          }
        }
      } else {
        // 私有库子主题创建
        const response = await apiClient.post('/users/knowledge-base/private/topics', {
          name: values.name,
          description: values.description,
          tagnamelist: values.tagnamelist,
          parentId: selectedParentTopic.topic_id // 设置父主题ID
        });
        
        console.log('创建私有库子主题成功:', response.data);
        message.success(`Sub-topic created successfully under "${selectedParentTopic.name}"`);
      }

      // 刷新知识库和主题数据 - 优先使用统一的刷新函数
      if (refreshKnowledgeBasesAndTopics) {
        console.log('[handleCreateSubTopic] 调用refreshKnowledgeBasesAndTopics刷新数据');
        await refreshKnowledgeBasesAndTopics();
      } else {
        console.log('[handleCreateSubTopic] refreshKnowledgeBasesAndTopics不可用，使用回退方法');
        // 回退到原来的方法
        await fetchPrivateKBTopics();
        
        // 如果是协作库，还需要刷新协作库的主题数据
        if (isCollaborative && selectedParentTopic.context && selectedParentTopic.context.kbId) {
          try {
            console.log('[handleCreateSubTopic] 刷新协作库主题数据:', selectedParentTopic.context.kbId);
            // 获取最新的协作库主题数据
            const updatedTopics = await apiHelpers.fetchCollaborativeKBTopics(selectedParentTopic.context.kbId);
            
            // 更新协作库主题数据
            if (setCollaborativeKBTopics) {
              setCollaborativeKBTopics(prev => ({
                ...prev,
                [selectedParentTopic.context.kbId]: updatedTopics
              }));
              console.log('[handleCreateSubTopic] 协作库主题数据已更新:', selectedParentTopic.context.kbId, updatedTopics);
            }
          } catch (refreshError) {
            console.error('[handleCreateSubTopic] 刷新协作库主题数据失败:', refreshError);
          }
        }
      }
      
      // 重置表单并关闭模态框
      createSubTopicForm.resetFields();
      setIsCreateSubTopicVisible(false);
      setSelectedParentTopic(null);
    } catch (error) {
      console.error('创建子主题失败:', error);
      message.error('Creation failed: ' + (error.response?.data?.message || error.message));
    }
  };

  // 取消创建子主题
  const handleCancelCreateSubTopic = () => {
    createSubTopicForm.resetFields();
    setIsCreateSubTopicVisible(false);
    setSelectedParentTopic(null);
  };

  // 开始创建子主题
  const handleStartCreateSubTopic = (parentTopic) => {
    setSelectedParentTopic(parentTopic);
    setIsCreateSubTopicVisible(true);
  };

  return {
    toggleTopicExpanded,
    handleRenameTopic,
    handleConfirmRename,
    handleCancelRename,
    handleDeleteTopic,
    handleCreateTopic,
    handleCancelCreateTopic,
    handleCreateSubTopic,
    handleCancelCreateSubTopic,
    handleStartCreateSubTopic
  };
}; 